import { usePatientFilesContext } from "containers/PatientFilesProvider";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  FILE_DELETION_TYPES,
  FILE_SCOPE,
  FILE_TABLE_ACTION_DELETE,
  FILE_TABLE_ACTION_SHARE,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { activateEligibilityFiles } from "core/model/utils/featureFlags";
import {
  Auction,
  AvailableFileRow,
  ClinicalConsultation,
  FileParams,
  FileState,
  Filev2,
  Patient,
} from "core/types";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { StyledPaper } from "ds_legacy/components/Tables/GeneralTable/styles";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { Button } from "ds/ui";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import useProductTour, { PRODUCT_TOURS } from "dsl/molecules/useProductTour";
import AvailableFiles from "dsl/organisms/FileTables/AvailableFiles";
import ReceivedFiles from "dsl/organisms/FileTables/ReceivedFiles";
import UnavailableFiles from "dsl/organisms/FileTables/UnavailableFiles";
import { useFileTransferAllowed } from "dsl/organisms/FileTables/useFileTransferAllowed";
import { FileDeleteModal } from "dsl/organisms/Modals/FileDeleteModal";
import { FileShareModal } from "dsl/organisms/Modals/FileSharingModal";
import { UploadFilesModal } from "dsl/organisms/UploadFilesModal";
import { SparklesIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { DocsSection } from "./DocsSection";
import { ExtractServiceUnavailableBanner } from "./ExtractServiceUnavailableBanner";
import { FileDetailsModal } from "./FileDetails";
import { useGetFilesFeatures } from "./useGetFilesFeatures";
import { useTrackFilesCount } from "./useTrackFilesCount";
import { useVoiceTranscriptFiles } from "./useVoiceTranscriptFiles";

export const useCanExtractAllFiles = (files: FileState[]) => {
  const { extractOcrStatus, utils } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  if (!isDocsAndExtractActive && !isDischargeLetterActive) {
    return false;
  }

  // no files to extract
  if (!files.length) return false;

  if (extractOcrStatus.error || !extractOcrStatus.isInitialResponseReceived) {
    return false;
  }

  // Check if there are any files (including HIS files) that are not yet
  // processing or extracted
  return files.some(
    (file) =>
      !utils.isFileExtractionProcessing(file) && !utils.isFileExtracted(file),
  );
};

export default function PatientFiles({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  patient: Patient;
}) {
  const { isTablet } = useMedia();
  const loggedCareseeker = useLoggedCareseeker();

  const { isDischargeLetterActive, isDocsAndExtractActive, isRedesignActive } =
    useIsDocsAndExtractActive();
  const translations = useTranslations();

  let paramId: FileParams;
  if (auction) paramId = `patient_id=${patient.id}&auction_id=${auction.id}`;
  else if (consultation)
    paramId = `patient_id=${patient.id}&clinical_consultation_id=${consultation.id}`;
  else paramId = `patient_id=${patient.id}`;

  const paramIdSideEffects: FileParams = isRedesignActive
    ? `${paramId}&without_form_side_effects=true`
    : paramId;

  const params: FileParams = activateEligibilityFiles
    ? (`${paramIdSideEffects}&scope=${FILE_SCOPE.PATIENT_FILES}` as FileParams)
    : paramIdSideEffects;

  const {
    action,
    closeDetails,
    detailFileId,
    handleDeleteFile,
    handleReset,
    handleShareFile,
    handleUploadFile,
    handleUploadFilesHIS,
    mutationProgress,
    openDetails,
    setAction,
  } = usePatientFilesContext({
    auction,
    externalId: patient.external_id,
    params,
  });

  const {
    availableFiles,
    getFilesHISProgress,
    queryProgress,
    receivedFiles,
    unavailableFiles,
    uploadedFilesV2,
  } = useGetFilesFeatures({
    action,
    externalId: patient.external_id,
    params,
    skipForms: isRedesignActive,
  });

  const { extractOcr, extractOcrStatus } = useExtractDocs();
  const canExtractAllFiles = useCanExtractAllFiles(uploadedFilesV2 ?? []);
  const voiceTranscriptFiles = useVoiceTranscriptFiles();

  const detailFile = useMemo<AvailableFileRow | null>(
    () =>
      detailFileId == null
        ? null
        : uploadedFilesV2?.find((f) => f.id === detailFileId) ??
          voiceTranscriptFiles.find((f) => f.id === detailFileId) ??
          null,
    [uploadedFilesV2, voiceTranscriptFiles, detailFileId],
  );

  const handleDelete = useCallback(
    async (id?: number) => {
      await handleDeleteFile(id);
      if (
        action?.data &&
        "fileDeletionType" in action.data &&
        action.data.fileDeletionType === FILE_DELETION_TYPES.EXTRACTED_ONLY
      ) {
        await extractOcrStatus.refetch();
      }
    },
    [action, handleDeleteFile, extractOcrStatus],
  );

  useTrackFilesCount({ receivedFiles, unavailableFiles, uploadedFilesV2 });
  useProductTour({
    skip: isRedesignActive,
    tour: PRODUCT_TOURS.files_page,
  });
  useProductTour({
    skip: !uploadedFilesV2?.length || isRedesignActive,
    tour: PRODUCT_TOURS.available_file_to_share,
  });
  const { canReceiveFiles } = useFileTransferAllowed(patient.careseeker);

  return (
    <>
      <VerticalLayout boxSizing="border-box" justify="center" width="100%">
        <h1 className="text-heading-3xl font-bold">
          {translations.fileSection.headline}
        </h1>
        <DocsSection
          auction={auction}
          consultation={consultation}
          files={uploadedFilesV2}
          patient={patient}
        />
        {(isDocsAndExtractActive || isDischargeLetterActive) && (
          <ExtractServiceUnavailableBanner className="mt-4 mb-4 w-full" />
        )}
        <StyledPaper
          $isTablet={isTablet}
          elevation={isTablet ? 0 : undefined}
          square={isTablet}
        >
          <AvailableFiles
            careseekerId={loggedCareseeker?.id || null}
            files={uploadedFilesV2}
            isConsultation={!!consultation}
            queryProgress={queryProgress}
            setAction={setAction}
            titleButton={
              <div className="flex items-center gap-4">
                <UploadFilesModal
                  action={action as GeneralListAction<Filev2> | undefined}
                  availableFiles={availableFiles}
                  getFilesHISProgress={getFilesHISProgress}
                  handleSubmit={handleUploadFile}
                  handleUploadFilesHIS={handleUploadFilesHIS}
                  isDisabled={false}
                  mutationProgress={mutationProgress}
                  reset={handleReset}
                  setAction={setAction}
                  unavailableFiles={unavailableFiles}
                />
                {isDocsAndExtractActive || isDischargeLetterActive ? (
                  <Button
                    color="primary"
                    id="extract-all-files"
                    isDisabled={!canExtractAllFiles}
                    size="sm"
                    startContent={<SparklesIcon />}
                    variant="solid"
                    onPress={() => {
                      extractOcr.handleExtractAll({
                        files: uploadedFilesV2 ?? [],
                      });
                    }}
                  >
                    {translations.extractDocs.filesTabChanges.buttonExtractAll}
                  </Button>
                ) : undefined}
              </div>
            }
            onOpenDetails={openDetails}
          />
        </StyledPaper>
        {!!unavailableFiles && (
          <StyledPaper $isTablet={isTablet}>
            <UnavailableFiles
              files={unavailableFiles}
              queryProgress={getFilesHISProgress}
            />
          </StyledPaper>
        )}
        {auction && (
          <StyledPaper
            $isTablet={isTablet}
            elevation={isTablet ? 0 : undefined}
            square={isTablet}
          >
            <ReceivedFiles
              canAccessPatientData
              canReceiveFiles={canReceiveFiles}
              careseekerId={loggedCareseeker?.id || null}
              files={receivedFiles}
              queryProgress={queryProgress}
              setAction={setAction}
            />
          </StyledPaper>
        )}
      </VerticalLayout>
      <FileShareModal
        file={action?.data as Filev2}
        handleShareFile={handleShareFile}
        isOpen={action?.type === FILE_TABLE_ACTION_SHARE}
        mutationProgress={mutationProgress}
        reset={handleReset}
        setAction={setAction}
      />
      <FileDeleteModal
        file={action?.data as FileState}
        handleDelete={handleDelete}
        isOpen={action?.type === FILE_TABLE_ACTION_DELETE}
        mutationProgress={mutationProgress}
        reset={handleReset}
      />
      <FileDetailsModal
        externalId={patient.external_id}
        file={detailFile}
        setAction={setAction}
        onClose={closeDetails}
      />
    </>
  );
}
